import { LoadingSpinner } from '@mgmresorts/wcl-core';
import React from 'react';

import { StyledBackdrop } from './Backdrop.styles';

export interface BackdropProps {
  active?: boolean;
  loading?: boolean;
  transitionDuration: number;
  backgroundColor: string;
  zIndex: number;
  onClick?(): void;
}

/**
 * Shows visible backdrop when `active` and invisible backdrop when `!active`.
 * If `!active`, it will show the backdrop
 * with the props that were used when backdrop had been `active`.
 *
 * This allows us to do a show and hide transition.
 *
 * Renders a <Backdrop /> component
 * @param {boolean} active - an optional param - to show/hide backdrop
 * @param {boolean} loading - an optional param - to show a spinner
 * @param {number} transitionDuration - to set the duration of transition
 * @param {string} backgroundColor - to set the backgroundColor of backdrop
 * @param {number} zIndex - to set the z-index of backdrop
 * @param {void} onClick - add a listener on backdrop
 */
export const Backdrop = ({ active, loading, transitionDuration, backgroundColor, zIndex, onClick }: BackdropProps) => {
  const [lastBackdrop, setLastBackdrop] = React.useState<{
    transitionDuration: number;
    backgroundColor: string;
    zIndex: number;
  }>({
    transitionDuration,
    backgroundColor,
    zIndex,
  });

  if (
    active &&
    (transitionDuration !== lastBackdrop.transitionDuration ||
      zIndex !== lastBackdrop.zIndex ||
      backgroundColor !== lastBackdrop.backgroundColor)
  ) {
    setLastBackdrop({
      transitionDuration,
      backgroundColor,
      zIndex,
    });
  }

  const currentBackdrop = active ? { transitionDuration, backgroundColor, zIndex } : lastBackdrop;

  return (
    <StyledBackdrop
      active={!!active}
      role='presentation'
      zIndex={currentBackdrop.zIndex}
      transitionDuration={currentBackdrop.transitionDuration}
      backgroundColor={currentBackdrop.backgroundColor}
      onClick={onClick}
      data-testid={Backdrop.selector()}
    >
      {loading && <LoadingSpinner />}
    </StyledBackdrop>
  );
};

Backdrop.defaultProps = {
  active: true,
  loading: false,
  transitionDuration: 300,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  onClick: undefined,
};

Backdrop.selector = (): string => 'Backdrop';
